import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api, type Node, humanBytes } from '../lib/api'
import { PageHeader, Button, Card, Select, Field, Modal, Input, ErrorBox, Spinner } from '../components/ui'
import { IconFolder, IconFile, IconTrash, IconPlus } from '../components/icons'

interface DirEntry {
  name: string
  is_dir: boolean
  size: number
  mode: number
  uid: number
  gid: number
  modified: string
}

export function FilesPage() {
  const qc = useQueryClient()
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const [nodeID, setNodeID] = useState<string>('')
  const [path, setPath] = useState<string>('/var/www')
  const [selected, setSelected] = useState<{ path: string; name: string } | null>(null)
  const [mkdirOpen, setMkdirOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<string[] | null>(null)
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!nodeID && nodes.length) setNodeID(nodes[0].id)
  }, [nodes, nodeID])

  // сброс checked при смене папки
  useEffect(() => { setChecked(new Set()) }, [path, nodeID])

  const { data: listing, isLoading, error } = useQuery({
    queryKey: ['files', nodeID, path],
    queryFn: async () => (await api.get<{ path: string; entries: DirEntry[] }>(
      `/files/list?node_id=${nodeID}&path=${encodeURIComponent(path)}`)).data,
    enabled: !!nodeID,
  })

  const del = useMutation({
    mutationFn: async (p: string) => api.delete('/files/remove', {
      data: { node_id: nodeID, path: p, recursive: true },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files', nodeID, path] }),
  })

  // массовое удаление
  const deleteChecked = async () => {
    if (checked.size === 0) return
    if (!confirm(`Удалить ${checked.size} элементов?`)) return
    for (const p of checked) {
      await api.delete('/files/remove', { data: { node_id: nodeID, path: p, recursive: true } })
    }
    setChecked(new Set())
    qc.invalidateQueries({ queryKey: ['files', nodeID, path] })
  }

  // массовый архив (POST /files/archive)
  const archiveChecked = async () => {
    if (checked.size === 0) return
    const name = prompt('Имя архива (без .tar.gz):', 'archive-' + Date.now())
    if (!name) return
    const out = (path.endsWith('/') ? path : path + '/') + name + '.tar.gz'
    await api.post('/files/archive', { node_id: nodeID, output: out, paths: Array.from(checked) })
    setChecked(new Set())
    qc.invalidateQueries({ queryKey: ['files', nodeID, path] })
  }

  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      for (const file of Array.from(files)) {
        const fd = new FormData()
        fd.append('node_id', nodeID)
        fd.append('path', (path.endsWith('/') ? path : path + '/') + file.name)
        fd.append('file', file)
        await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['files', nodeID, path] }),
  })

  const search = useMutation({
    mutationFn: async () => (await api.get<{ results: string[] }>(
      `/files/search?node_id=${nodeID}&path=${encodeURIComponent(path)}&query=${encodeURIComponent(searchQuery)}`)).data,
    onSuccess: (r) => setSearchResults(r.results),
  })

  const parent = () => {
    const p = path.replace(/\/+$/, '')
    if (p === '' || p === '/') return
    const parts = p.split('/')
    parts.pop()
    setPath(parts.length <= 1 ? '/' : parts.join('/'))
    setSelected(null)
  }

  const enter = (name: string) => {
    const p = (path.endsWith('/') ? path : path + '/') + name
    setPath(p)
    setSelected(null)
  }

  const download = (fullPath: string) => {
    const token = localStorage.getItem('beebro.token')
    // Токен через query — Bearer в GET-загрузке невозможно; альтернативно — открыть в новой вкладке.
    // Пока откроем через blob с fetch, чтобы Authorization header ушёл.
    fetch(`/api/v1/files/download?node_id=${nodeID}&path=${encodeURIComponent(fullPath)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fullPath.split('/').pop() || 'file'
        document.body.appendChild(a); a.click(); a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 500)
      })
  }

  const crumbs = path.split('/').filter(Boolean)

  return (
    <>
      <PageHeader
        title="Файлы"
        subtitle="Просмотр и редактирование файлов на ноде. Все действия выполняются через агента."
        actions={
          <>
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
              <IconPlus /> Загрузить файлы
            </Button>
            <Button variant="secondary" onClick={() => setMkdirOpen(true)}>
              <IconPlus /> Создать папку
            </Button>
          </>
        }
      />
      <input ref={fileInputRef} type="file" multiple hidden
             onChange={(e) => { if (e.target.files?.length) upload.mutate(e.target.files); e.target.value = '' }} />

      {/* Drag&drop zone — твой весь grid внутри */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false)
          if (e.dataTransfer.files.length) upload.mutate(e.dataTransfer.files)
        }}
        className={`transition ${dragOver ? 'ring-4 ring-blue-300 rounded-md' : ''}`}
      >

      {checked.size > 0 && (
        <div className="flex items-center gap-2 mb-3 p-2.5 bg-neutral-900 text-white rounded-md text-[13px]">
          <span className="font-medium">Выбрано: {checked.size}</span>
          <Button size="sm" variant="secondary" onClick={archiveChecked}>Архивировать</Button>
          <Button size="sm" variant="secondary" onClick={deleteChecked}>Удалить</Button>
          <Button size="sm" variant="ghost" onClick={() => setChecked(new Set())} className="text-white hover:bg-neutral-800">Отменить</Button>
          <span className="ml-auto text-[11px] text-neutral-400">Жми Ctrl/Cmd+A чтобы выделить всё</span>
        </div>
      )}


      {/* Панель управления */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="w-full sm:w-52">
          <Select value={nodeID} onChange={(e) => { setNodeID(e.target.value); setPath('/var/www'); setSelected(null); setSearchResults(null) }}>
            {nodes.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
          </Select>
        </div>
        <div className="flex-1 flex items-center gap-1 bg-white border border-neutral-200 rounded-md px-3 py-1.5 text-[13px] overflow-x-auto">
          <button className="text-neutral-500 hover:text-neutral-900" onClick={() => { setPath('/'); setSearchResults(null) }}>/</button>
          {crumbs.map((c, i) => {
            const p = '/' + crumbs.slice(0, i + 1).join('/')
            return (
              <div key={p} className="flex items-center gap-1">
                <span className="text-neutral-300">/</span>
                <button className="text-neutral-700 hover:text-neutral-900 hover:underline whitespace-nowrap"
                        onClick={() => { setPath(p); setSelected(null); setSearchResults(null) }}>{c}</button>
              </div>
            )
          })}
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) search.mutate() }}>
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                 placeholder="Поиск по имени…" className="w-48" />
          <Button variant="secondary" type="submit" disabled={search.isPending || !searchQuery.trim()}>
            {search.isPending ? '…' : 'Найти'}
          </Button>
          {searchResults && (
            <Button variant="ghost" onClick={() => setSearchResults(null)}>×</Button>
          )}
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
        {/* Список / Результаты поиска */}
        <Card className="overflow-hidden">
          {searchResults !== null ? (
            <div>
              <div className="px-4 py-2 border-b border-neutral-200 bg-neutral-50/60 text-[11px] uppercase text-neutral-500 tracking-wider">
                Найдено: {searchResults.length}
              </div>
              {searchResults.length === 0 && <div className="p-6 text-center text-neutral-400 text-[13px]">Ничего не найдено</div>}
              {searchResults.map((p) => (
                <button key={p} onClick={() => setSelected({ path: p, name: p.split('/').pop() || p })}
                        className="w-full text-left px-4 py-1.5 flex items-center gap-2 hover:bg-neutral-50 text-[12px] font-mono">
                  <IconFile className="text-neutral-400 shrink-0" />
                  <span className="truncate">{p}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              {isLoading && <div className="p-6 flex items-center gap-2 text-neutral-500 text-[13px]"><Spinner /> Загрузка…</div>}
              {error && <div className="p-6 text-red-600 text-[13px]">Ошибка: {(error as any)?.response?.data?.detail || (error as any).message}</div>}
              {!isLoading && listing && (
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50/60 text-[11px] uppercase text-neutral-500 tracking-wider">
                      <th className="text-left px-4 py-2">Имя</th>
                      <th className="text-right px-4 py-2">Размер</th>
                      <th className="text-right px-4 py-2">Изменён</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {path !== '/' && (
                      <tr className="hover:bg-neutral-50 cursor-pointer" onClick={parent}>
                        <td className="px-4 py-2 flex items-center gap-2 text-neutral-600">
                          <span className="w-4"></span>
                          <IconFolder className="text-neutral-400" /> ..
                        </td>
                        <td colSpan={3}></td>
                      </tr>
                    )}
                    {listing.entries.length > 0 && (
                      <tr className="bg-neutral-50/40 border-t border-neutral-100">
                        <td className="px-4 py-1">
                          <input type="checkbox"
                            checked={checked.size === listing.entries.length && listing.entries.length > 0}
                            onChange={() => {
                              if (checked.size === listing.entries.length) setChecked(new Set())
                              else setChecked(new Set(listing.entries.map(e => (path.endsWith('/') ? path : path + '/') + e.name)))
                            }}
                            className="h-4 w-4 rounded border-neutral-300 accent-black cursor-pointer" />
                        </td>
                        <td colSpan={3} className="text-[11px] text-neutral-500">
                          {checked.size > 0
                            ? <span>Выбрано: <b>{checked.size}</b></span>
                            : <span>Выделить всё</span>}
                        </td>
                      </tr>
                    )}
                    {listing.entries.map((e) => {
                      const full = (path.endsWith('/') ? path : path + '/') + e.name
                      const isChecked = checked.has(full)
                      return (
                        <tr key={e.name} className={`border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer ${
                          selected?.path === full ? 'bg-neutral-50' : ''
                        } ${isChecked ? 'bg-blue-50/60' : ''}`}
                          onClick={(ev) => {
                            if ((ev.target as HTMLElement).closest('input,button')) return
                            if (e.is_dir) enter(e.name)
                            else setSelected({ path: full, name: e.name })
                          }}>
                          <td className="px-4 py-1.5 flex items-center gap-2">
                            <input type="checkbox" checked={isChecked}
                              onChange={() => {
                                const s = new Set(checked)
                                if (s.has(full)) s.delete(full); else s.add(full)
                                setChecked(s)
                              }}
                              className="h-4 w-4 rounded border-neutral-300 accent-black cursor-pointer" />
                            {e.is_dir ? <IconFolder className="text-neutral-500 shrink-0" /> : <IconFile className="text-neutral-400 shrink-0" />}
                            <span className={e.is_dir ? 'font-medium' : ''}>{e.name}</span>
                          </td>
                          <td className="px-4 py-1.5 text-right text-neutral-500 tabular-nums">
                            {e.is_dir ? '—' : humanBytes(e.size)}
                          </td>
                          <td className="px-4 py-1.5 text-right text-neutral-500">{e.modified}</td>
                          <td className="px-2">
                            <div className="flex gap-0.5 justify-end">
                              {!e.is_dir && (
                                <button title="Скачать" onClick={(ev) => { ev.stopPropagation(); download(full) }}
                                        className="text-neutral-400 hover:text-neutral-900 p-1">
                                                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 4v12"/><path d="M6 12l6 6 6-6"/><path d="M4 20h16"/>
                                  </svg>
                                </button>
                              )}
                              <button title="Удалить" onClick={(ev) => { ev.stopPropagation(); if (confirm(`Удалить ${e.name}?`)) del.mutate(full) }}
                                      className="text-neutral-400 hover:text-red-600 p-1">
                                <IconTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                    {listing.entries.length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-neutral-400">Пусто</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </Card>

        {/* Просмотр/редактор */}
        {selected
          ? <FileEditor nodeID={nodeID} filePath={selected.path}
              onDownload={() => download(selected.path)}
              onDeleted={() => { setSelected(null); qc.invalidateQueries({ queryKey: ['files', nodeID, path] }) }} />
          : <Card className="p-8 text-center text-neutral-400 text-[13px]">Выберите файл слева для просмотра или редактирования</Card>}
      </div>

      </div>

      {mkdirOpen && <MkdirModal nodeID={nodeID} parent={path} onClose={() => setMkdirOpen(false)}
        onCreated={() => { setMkdirOpen(false); qc.invalidateQueries({ queryKey: ['files', nodeID, path] }) }} />}
    </>
  )
}

function FileEditor({ nodeID, filePath, onDeleted, onDownload }: {
  nodeID: string; filePath: string; onDeleted: () => void; onDownload: () => void
}) {
  const [text, setText] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [err, setErr] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['file', nodeID, filePath],
    queryFn: async () => (await api.get<{ text?: string; binary_base64?: string; size: number; mode: number; truncated: boolean }>(
      `/files/read?node_id=${nodeID}&path=${encodeURIComponent(filePath)}`)).data,
  })

  useEffect(() => {
    if (data?.text !== undefined) { setText(data.text); setDirty(false) }
    else if (data?.binary_base64) { setText(null) }
  }, [data])

  const save = useMutation({
    mutationFn: async () => api.post('/files/write', { node_id: nodeID, path: filePath, text }),
    onSuccess: () => { setDirty(false); setErr('') },
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })

  const del = useMutation({
    mutationFn: async () => api.delete('/files/remove', { data: { node_id: nodeID, path: filePath, recursive: false } }),
    onSuccess: onDeleted,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })

  if (isLoading) return <Card className="p-6"><Spinner /></Card>
  if (data?.binary_base64) {
    const ext = (filePath.split('.').pop() || '').toLowerCase()
    const isImage = ['png','jpg','jpeg','gif','webp','svg','bmp','ico'].includes(ext)
    const isPdf = ext === 'pdf'
    const mime = ext === 'svg' ? 'image/svg+xml' : ext === 'pdf' ? 'application/pdf' : `image/${ext === 'jpg' ? 'jpeg' : ext}`
    const src = (isImage || isPdf) ? `data:${mime};base64,${data.binary_base64}` : ''
    return (
      <Card className="p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <div className="text-[13px] font-mono truncate">{filePath}</div>
            <div className="text-[11px] text-neutral-500">{humanBytes(data.size)} · {isImage ? 'картинка' : isPdf ? 'PDF' : 'бинарный файл'}</div>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onDownload}>⭳</Button>
            <Button size="sm" variant="ghost" onClick={() => confirm('Удалить файл?') && del.mutate()}><IconTrash /></Button>
          </div>
        </div>
        {isImage && (
          <div className="flex items-center justify-center bg-[url(data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2220%22%20height%3D%2220%22%3E%3Crect%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f3f4f6%22/%3E%3Crect%20x%3D%2210%22%20y%3D%2210%22%20width%3D%2210%22%20height%3D%2210%22%20fill%3D%22%23f3f4f6%22/%3E%3C/svg%3E)] rounded-md min-h-[300px] p-4">
            <img src={src} alt={filePath} className="max-w-full max-h-[500px] object-contain rounded shadow" />
          </div>
        )}
        {isPdf && (
          <iframe src={src} title={filePath} className="w-full h-[500px] border border-neutral-200 rounded" />
        )}
        {!isImage && !isPdf && (
          <div className="text-[13px] text-neutral-500">Превью недоступно — скачайте для просмотра.</div>
        )}
      </Card>
    )
  }

  return (
    <Card className="flex flex-col">
      <div className="px-4 py-2.5 border-b border-neutral-200 bg-neutral-50/60 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[13px] font-mono truncate">{filePath}</div>
          <div className="text-[11px] text-neutral-500">{humanBytes(data?.size || 0)}{data?.truncated && ' (обрезано)'}</div>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={onDownload} title="Скачать">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 4v12"/><path d="M6 12l6 6 6-6"/><path d="M4 20h16"/>
            </svg>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => confirm('Удалить файл?') && del.mutate()}><IconTrash /></Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
            {save.isPending ? 'Сохраняем…' : dirty ? 'Сохранить' : 'Сохранено'}
          </Button>
        </div>
      </div>
      <textarea
        value={text ?? ''}
        onChange={(e) => { setText(e.target.value); setDirty(true) }}
        spellCheck={false}
        className="w-full min-h-[400px] p-4 font-mono text-[12px] leading-relaxed border-0 focus:outline-none resize-none bg-neutral-50/30"
      />
      {err && <div className="p-3 border-t border-red-200 bg-red-50 text-[12px] text-red-700">{err}</div>}
    </Card>
  )
}

function MkdirModal({ nodeID, parent, onClose, onCreated }: {
  nodeID: string; parent: string; onClose: () => void; onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => {
      const path = (parent.endsWith('/') ? parent : parent + '/') + name
      return api.post('/files/mkdir', { node_id: nodeID, path, mode: 0o755 })
    },
    onSuccess: onCreated,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title={`Создать папку в ${parent}`}>
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Имя папки">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="new-folder" required autoFocus />
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>Создать</Button>
        </div>
      </form>
    </Modal>
  )
}
